import { createContext, useContext, useState, useCallback, useEffect, ReactNode, createElement } from "react";
import en from "./en";
import pl from "./pl";
import { apiFetch } from "../api/apiFetch";

type Language = "en" | "pl";
type Translations = Record<string, string>;

const translations: Record<Language, Translations> = { en, pl };

// This browser's last language. Still the source of truth for the
// signed-out screens (login, magic-link) and for anyone whose account
// has no saved preference; a saved preference overrides it at login
// (pages/login.tsx completeLogin).
const STORAGE_KEY = "jli-lang";

export const isLanguage = (value: unknown): value is Language =>
    value === "en" || value === "pl";

const getStoredLanguage = (): Language => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (isLanguage(stored)) return stored;
    } catch {
        // localStorage not available
    }
    return "en";
};

// A signed-in user's choice is saved on their account (users.language,
// PATCH /login/user/language) so it follows them to the next device or
// kiosk instead of living in one browser. Signed-out toggles (the login
// screen) stay local — there is no account to save against yet, and the
// one that logs in next brings its own preference. Best-effort: the UI
// has already switched, and the next toggle or login re-syncs.
const persistLanguage = (lang: Language) => {
    let loggedIn = false;
    try {
        loggedIn = localStorage.getItem("isLoggedIn") === "true";
    } catch {
        // localStorage not available
    }
    if (!loggedIn) return;
    try {
        apiFetch("/login/user/language", {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ language: lang }),
        }).catch(() => { /* best-effort, see above */ });
    } catch {
        // best-effort, see above
    }
};

interface SetLanguageOptions {
    /** false = apply locally only. Used when the value came FROM the
     *  server (login payload), so it isn't echoed straight back. */
    persist?: boolean;
}

interface LanguageContextValue {
    language: Language;
    setLanguage: (lang: Language, options?: SetLanguageOptions) => void;
    t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue>({
    language: "en",
    setLanguage: () => {},
    t: (key) => key,
});

export const useTranslation = () => useContext(LanguageContext);

interface LanguageProviderProps {
    children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
    const [language, setLanguageState] = useState<Language>(getStoredLanguage);

    const setLanguage = useCallback((lang: Language, options?: SetLanguageOptions) => {
        setLanguageState(lang);
        try {
            localStorage.setItem(STORAGE_KEY, lang);
        } catch {
            // localStorage not available
        }
        if (options?.persist !== false) persistLanguage(lang);
    }, []);

    // Keep <html lang> in step so the browser hyphenates Polish headings
    // (`hyphens: auto`) and assistive tech reads them in the right voice.
    useEffect(() => {
        try {
            document.documentElement.lang = language;
        } catch {
            // no DOM (tests)
        }
    }, [language]);

    const t = useCallback(
        (key: string, params?: Record<string, string | number>): string => {
            let text = translations[language][key] ?? translations.en[key] ?? key;
            if (params) {
                Object.entries(params).forEach(([k, v]) => {
                    text = text.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
                });
            }
            return text;
        },
        [language]
    );

    return createElement(
        LanguageContext.Provider,
        { value: { language, setLanguage, t } },
        children
    );
};

export type { Language };
